import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import * as XLSX from 'xlsx';

import { redeemStatusLabel } from '../components/couponDetail/constants';

// ─────────────────────────────────────────────
// "번호 기입폼" 엑셀 업로드 파서/분류기.
// 기입폼(deliveredCouponPhoneExcel · includeUnsent)을 채워 받은 파일을
// 쿠폰번호 기준으로 매칭해 확인번호(additionalRecipientPhone)와 메모를
// 일괄 저장 대상으로 분류한다. 행 순서는 신뢰하지 않는다(정렬·행 삭제 허용).
// 번호는 빈 칸 채우기 전용 — 수신 신청 이력(recipient/renewal 번호)이 있거나
// 확인번호가 이미 지정된 쿠폰은 자동 제외한다(덮어쓰기 없음).
// 메모는 기입 시 변경(수령인 이름 기록 용도) — 빈 칸은 기존 메모 유지라
// 엑셀 업로드로 메모가 지워지는 일은 없다. 번호가 차단된 쿠폰도 메모는 반영.
// ─────────────────────────────────────────────

const CODE_HEADER = '쿠폰번호';
const PHONE_HEADER = '수령번호';
const MEMO_HEADER = '메모';
/** 통합 기입폼(여러 주문 합본) 구분 열 — 있으면 다른 주문 쿠폰 행을 조용히 제외한다 */
const ORDER_HEADER = '주문';

export type PhoneFormRowStatus =
  | 'fill' // 확인번호 신규 기입 — 저장 대상
  | 'memoOnly' // 번호 기입 없음 — 메모만 변경
  | 'same' // 기존 확인번호와 동일 — 변경 없음
  | 'blockedExisting' // 확인번호 이미 지정됨 — 자동 제외(덮어쓰기 없음)
  | 'blockedApplied' // 수신 신청 번호 존재 — 자동 제외
  | 'blockedStatus' // 사용가능(Available) 상태가 아님 — 제외
  | 'invalidPhone' // 휴대폰 번호 형식 오류
  | 'unknownCode'; // 이 주문에 없는 쿠폰번호

export interface PhoneFormUploadRow {
  code: string;
  /** 엑셀 원문 (형식 오류 표시용) */
  rawPhone: string;
  /** 정규화된 저장값 (형식 오류·미매칭이면 없음) */
  phone?: string;
  coupon?: GiftCouponSummary;
  status: PhoneFormRowStatus;
  reason: string;
  /** 메모 변경 — 메모 칸이 기입됐고 기존 메모와 다를 때만 존재. from은 되돌리기용 */
  memoChange?: { from: string; to: string };
}

export interface PhoneFormParseResult {
  rows: PhoneFormUploadRow[];
  /** 번호 미기입(빈 칸·마커·마스킹)이고 메모 변경도 없어 목록에서 제외한 행 수 */
  skippedUnfilled: number;
  /** 같은 쿠폰번호가 여러 행 — 마지막 행만 남기고 버린 수 */
  duplicateDropped: number;
  /**
   * 통합 기입폼("주문" 열 존재)에서 이 목록에 없는 쿠폰번호 행 수 —
   * 다른 주문 건이므로 오류로 나열하지 않고 조용히 제외한다.
   * (주문별 폼에는 "주문" 열이 없어 기존처럼 미매칭 오류로 표시)
   */
  otherOrderSkipped: number;
}

/**
 * 엑셀 셀 → 저장용 휴대폰 번호. 셀 서식이 숫자로 바뀌면 앞 0이 떨어져 오므로
 * (01012345678 → 1012345678) '1'로 시작하는 9~10자리는 0을 복원한다.
 * 휴대폰(01x) 형식이 아니면 null.
 */
export const normalizeFormPhone = (raw: string): string | null => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const restored =
    digits.startsWith('1') && (digits.length === 9 || digits.length === 10)
      ? `0${digits}`
      : digits;
  return /^01[016789]\d{7,8}$/.test(restored) ? restored : null;
};

/**
 * 기입 안 된 칸 — 빈 값, 기발송 마스킹(010-****-5678), 숫자 없는 칸('▶ 번호기입요망' 마커).
 * 마커를 지우지 않고 옆에 번호를 적어도 숫자가 있으면 기입으로 취급한다.
 */
const isUnfilledCell = (raw: string): boolean => {
  const t = raw.trim();
  return !t || t.includes('*') || !/\d/.test(t);
};

/** 저장 시 이 번호를 밀어내게 되는 기존 번호 — 차단 사유 표시용 */
export const existingPhoneOf = (c: GiftCouponSummary | undefined): string =>
  c?.recipient?.phoneNumber ||
  c?.renewalRecipientPhone ||
  c?.additionalRecipientPhone ||
  '';

/**
 * 기입폼 엑셀(ArrayBuffer)을 파싱해 쿠폰별 저장 대상/차단 사유로 분류한다.
 * 폼이 아니거나 시트·데이터가 없으면 사용자 안내용 메시지로 throw.
 */
export const parsePhoneFormWorkbook = (
  data: ArrayBuffer,
  coupons: GiftCouponSummary[],
): PhoneFormParseResult => {
  const wb = XLSX.read(new Uint8Array(data), { type: 'array' });
  const firstSheetName = wb.SheetNames[0];
  const ws = firstSheetName ? wb.Sheets[firstSheetName] : undefined;
  if (!ws) throw new Error('엑셀에서 시트를 찾을 수 없습니다.');

  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
    defval: '',
  });
  const firstRow = json[0];
  if (!firstRow) throw new Error('엑셀에 데이터 행이 없습니다.');
  if (!(CODE_HEADER in firstRow) || !(PHONE_HEADER in firstRow))
    throw new Error(
      `"${CODE_HEADER}"·"${PHONE_HEADER}" 컬럼이 없습니다 — "번호 기입폼" 엑셀을 그대로 채워서 올려주세요.`,
    );

  const hasMemoColumn = MEMO_HEADER in firstRow;
  const isCombinedForm = ORDER_HEADER in firstRow;

  // 같은 쿠폰번호가 여러 행이면 마지막 행이 이긴다 (수정하며 아래에 다시 쓴 경우).
  let duplicateDropped = 0;
  const entries = new Map<string, { rawPhone: string; rawMemo: string }>();
  for (const row of json) {
    const code = String(row[CODE_HEADER] ?? '').trim();
    if (!code) continue;
    if (entries.has(code)) duplicateDropped += 1;
    entries.set(code, {
      rawPhone: String(row[PHONE_HEADER] ?? ''),
      rawMemo: hasMemoColumn ? String(row[MEMO_HEADER] ?? '') : '',
    });
  }

  const byCode = new Map<string, GiftCouponSummary>();
  for (const c of coupons) byCode.set(c.code.trim(), c);

  let skippedUnfilled = 0;
  let otherOrderSkipped = 0;
  const rows: PhoneFormUploadRow[] = [];
  for (const [code, cell] of Array.from(entries.entries())) {
    const { rawPhone, rawMemo } = cell;
    const coupon = byCode.get(code);
    // 통합폼의 다른 주문 쿠폰 — 오류가 아니라 "여기 소관이 아님"이므로 조용히 센다.
    // (해당 주문에서 같은 파일을 올리면 그쪽 건만 골라 저장된다)
    if (!coupon && isCombinedForm) {
      otherOrderSkipped += 1;
      continue;
    }
    const phoneFilled = !isUnfilledCell(rawPhone);
    // 메모 변경 — 기입돼 있고 기존 메모와 다를 때만. 빈 칸은 기존 메모 유지.
    const memoNew = rawMemo.trim();
    const memoChange =
      coupon && memoNew && memoNew !== (coupon.memo?.trim() ?? '')
        ? { from: coupon.memo ?? '', to: memoNew }
        : undefined;

    if (!phoneFilled && !memoChange) {
      // 미매칭인데 메모만 적힌 행은 버리지 않고 미매칭으로 보여준다.
      if (!coupon && memoNew) {
        rows.push({
          code,
          rawPhone,
          status: 'unknownCode',
          reason: '이 주문에 없는 쿠폰번호',
        });
      } else {
        skippedUnfilled += 1;
      }
      continue;
    }
    if (!coupon) {
      rows.push({
        code,
        rawPhone,
        status: 'unknownCode',
        reason: '이 주문에 없는 쿠폰번호',
      });
      continue;
    }
    if (!phoneFilled) {
      rows.push({
        code,
        rawPhone,
        coupon,
        status: 'memoOnly',
        reason: '번호 기입 없음',
        memoChange,
      });
      continue;
    }
    const phone = normalizeFormPhone(rawPhone);
    if (!phone) {
      rows.push({
        code,
        rawPhone,
        coupon,
        status: 'invalidPhone',
        reason: '휴대폰 번호 형식이 아님',
        memoChange,
      });
      continue;
    }
    // 이미 "수신하겠다"고 신청된 쿠폰은 항상 차단 — 고객이 직접 확정한 번호
    // (나중에받기·번호인증·연장 신청)를 엑셀 업로드가 덮어쓰지 못하게 한다.
    if (coupon.recipient?.phoneNumber || coupon.renewalRecipientPhone) {
      rows.push({
        code,
        rawPhone,
        phone,
        coupon,
        status: 'blockedApplied',
        reason: coupon.recipient?.phoneNumber
          ? '수신 신청된 번호 있음'
          : '연장 수신 신청됨',
        memoChange,
      });
      continue;
    }
    if (coupon.redeemStatus !== GiftCouponRedeemStatus.Available) {
      rows.push({
        code,
        rawPhone,
        phone,
        coupon,
        status: 'blockedStatus',
        reason: `${redeemStatusLabel[coupon.redeemStatus]} 상태`,
        memoChange,
      });
      continue;
    }
    const existing = (coupon.additionalRecipientPhone ?? '').replace(/\D/g, '');
    if (existing === phone) {
      rows.push({
        code,
        rawPhone,
        phone,
        coupon,
        status: 'same',
        reason: '기존 확인번호와 동일',
        memoChange,
      });
    } else if (existing) {
      rows.push({
        code,
        rawPhone,
        phone,
        coupon,
        status: 'blockedExisting',
        reason: '확인번호 이미 지정됨',
        memoChange,
      });
    } else {
      rows.push({
        code,
        rawPhone,
        phone,
        coupon,
        status: 'fill',
        reason: '',
        memoChange,
      });
    }
  }

  // 저장 대상이 위로, 같은 상태끼리는 쿠폰 순번대로.
  const priority: Record<PhoneFormRowStatus, number> = {
    fill: 0,
    memoOnly: 1,
    same: 2,
    blockedExisting: 3,
    blockedApplied: 4,
    blockedStatus: 5,
    invalidPhone: 6,
    unknownCode: 7,
  };
  rows.sort(
    (a, b) =>
      priority[a.status] - priority[b.status] ||
      (a.coupon?.seq ?? 0) - (b.coupon?.seq ?? 0),
  );

  return { rows, skippedUnfilled, duplicateDropped, otherOrderSkipped };
};
